import { mapBriefToJobCardProps, getInitials } from './mapBriefToJobCardProps';
import { formatWhereLine, splitWorkTypeRemote } from './formatWhereLine';
import {
  getMatchBand,
  truncateReasonToTwoSentences,
} from 'helpers/ariMatchBand';
import { calendarDaysAgoLabel } from 'helpers/postedAgo';
import { withTwineUtm } from 'helpers/withTwineUtm';
import { parseAriJobId } from './saveJobBody';

/**
 * Where-line + work-type chip for Ari slim/external cards (no Twine brief).
 *
 * @param {Object} job
 * @returns {{ where: Object, workType: string }}
 */
function whereAndWorkTypeFromAriJob(job) {
  const { workType, remoteFromType } = splitWorkTypeRemote(job.type || '');
  const location = String(job.location || '').trim();
  // "Remote", "Remote - UK", "Remote (London, UK)" → remote + trailing place.
  const remoteMatch = location.match(/^remote\b[\s\-–—:,|·•(]*(.*?)[\s)]*$/i);
  const locationIsRemote = Boolean(remoteMatch);
  const isRemote =
    remoteFromType || locationIsRemote || Boolean(job._remoteQuery);

  return {
    where: formatWhereLine({
      remote: isRemote,
      location: locationIsRemote ? remoteMatch[1] : location,
      settingUnknown: true,
    }),
    workType,
  };
}

/**
 * Board display name from an external job URL.
 * @param {string} url
 * @returns {string} e.g. "LinkedIn", or "" when unknown
 */
export function getBoardLabelFromUrl(url) {
  if (!url) {
    return '';
  }
  try {
    const host = new URL(url).hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'linkedin.com' || host.endsWith('.linkedin.com')) {
      return 'LinkedIn';
    }
  } catch (e) {
    // ignore
  }
  return '';
}

/**
 * Map an Ari stream/cache job card into JobCard view props + ari block.
 * Returns null when a match score is present but below the suppression
 * threshold. Jobs without a score (e.g. email /ari/job landing) still map;
 * the match-band chip is omitted.
 *
 * @param {Object} job
 * @param {Object|null} user
 * @returns {Object|null}
 */
export function mapAriJobToJobCardProps(job, user = null) {
  const matchProvided = job?.match != null && job?.match !== '';
  const band = matchProvided ? getMatchBand(job.match) : null;
  if (matchProvided && !band) {
    return null;
  }

  const reason = truncateReasonToTwoSentences(job.reason || '');
  const description = job.desc || job.description || '';
  const ari = {
    ...(band ? { band } : {}),
    reason,
    description,
  };

  if (job.onTwine && job.brief) {
    const mapped = mapBriefToJobCardProps(job.brief, user);
    return {
      ...mapped,
      brief: job.brief,
      ari,
      showSave: true,
      saveEntryPoint: 'ari',
    };
  }

  if (job.onTwine) {
    // Slim legacy card without brief — still show something, but no Save.
    const onTwinePosted = calendarDaysAgoLabel(job.postedAt);
    const { where, workType } = whereAndWorkTypeFromAriJob(job);
    return {
      id: job.id,
      jobUrl: job.url || '#',
      source: job.easyApply === false ? 'twine' : 'easyApply',
      easyApply: job.easyApply !== false,
      status: job.isPriority ? 'priority' : 'open',
      urgent: false,
      title: job.title || '',
      client: {
        name: job.company || '',
        owner: null,
        avatar: null,
        initials: getInitials(job.company || ''),
        isPrivate: false,
        location: '',
        lastActiveAt: '',
        activityLabel: null,
        show: Boolean(job.company),
      },
      where,
      chips: {
        setting: '',
        budget: job.rate || 'Open to offers',
        role: '',
        experience: '',
        workType,
        readyToHire: false,
      },
      excerpt: '',
      closesAt: '',
      listedAt: job.postedAt || '',
      timeLine: onTwinePosted ? `Posted ${onTwinePosted}` : '',
      footerMeta: '',
      canApply: true,
      saved: false,
      pipelineEntryId: null,
      ariJobId: null,
      cta: {
        label: 'View job',
        href: job.url || '#',
        external: false,
      },
      externalHostname: '',
      boardLabel: null,
      brief: null,
      ari,
      showSave: false,
      saveEntryPoint: 'ari',
    };
  }

  const boardLabel = getBoardLabelFromUrl(job.url);
  const postedAt = job.postedAt;
  const postedLabel = calendarDaysAgoLabel(postedAt);
  const hostname = (() => {
    try {
      return job.url ? new URL(job.url).hostname.replace(/^www\./i, '') : '';
    } catch (e) {
      return '';
    }
  })();

  const ctaLabel = boardLabel ? `View on ${boardLabel}` : 'View on their site';
  const footerBoard = boardLabel || 'their site';
  const externalUrl = job.url ? withTwineUtm(job.url) : '#';
  // Only ingested external_jobs (fj-{pk}) are saveable; live FJ full-time
  // cards use fj-live-* / canSave:false and hide Save for now.
  const ariJobId = parseAriJobId(job.id);
  const canSave = job.canSave !== false && ariJobId != null;
  const { where, workType } = whereAndWorkTypeFromAriJob(job);

  return {
    id: job.id,
    jobUrl: externalUrl,
    source: 'external',
    easyApply: false,
    status: 'none',
    urgent: false,
    title: job.title || '',
    client: {
      name: job.company || '',
      owner: null,
      avatar: null,
      initials: getInitials(job.company || ''),
      isPrivate: false,
      location: '',
      lastActiveAt: '',
      activityLabel: null,
      show: Boolean(job.company),
    },
    where,
    chips: {
      setting: '',
      budget: job.rate || 'Pay not stated',
      role: '',
      experience: '',
      workType,
      readyToHire: false,
    },
    excerpt: '',
    closesAt: '',
    listedAt: postedAt || '',
    timeLine: postedLabel ? `Found ${postedLabel}` : '',
    footerMeta: postedLabel ? `Listed ${postedLabel} on ${footerBoard}` : '',
    canApply: false,
    saved: Boolean(job.saved_by_current_user || job.saved),
    pipelineEntryId:
      job.pipeline_entry_id != null ? job.pipeline_entry_id : null,
    ariJobId: canSave ? job.id : null,
    cta: {
      label: ctaLabel,
      href: externalUrl,
      external: true,
    },
    externalHostname: hostname,
    boardLabel: boardLabel || 'External',
    brief: null,
    ari,
    showSave: canSave,
    saveEntryPoint: 'ari',
  };
}
